import React, {useState, useCallback, useMemo, useEffect, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {NavBar, Toast, Dialog, Badge, Divider, Card} from 'antd-mobile';
import {t} from 'i18next';
import DynamSoftScanComponent from 'components/ScanComponent/DynamSoftScanComponent';
import {timestampToDate} from '../../../utils/dateTimeUtils';
import dayjs from 'dayjs';
import {TaskPath, DspPath, DeliveringPath} from '../../../enums/DeliveryNavigatePathEnum';
import { getDeliveringListPath } from '../../../utils/deliveryRouteUtils';
import {
    identifyParcel,
    identifyAndTransferParcelV2,
    ParcelIdentificationResult,
    ParcelIdentificationCode,
    IdentifyAndTransferParcelResult,
    ParcelInfo
} from '../graphql/mutations/identifyParcel';
import { formatStopNumber } from '../../../utils/stopNumberUtils';
import {maskPhoneNumber} from "../../../utils/phoneUtils";

// 类型定义
interface TransferSuccessData {
    parcel?: ParcelInfo;
    recommendDriverName?: string;
    previousRouteId?: string;
    previousDriverName?: string;
    scanTime?: string;
}

// 常量定义
const CONSTANTS = {
    TOAST_DURATION: 3000,
    SCAN_LOCATION: {latitude: 0, longitude: 0},
    DIALOG_CLASSES: {
        ERROR: 'parcel-identification-error-dialog',
        TRANSFER: 'parcel-identification-transfer-dialog',
    }
} as const;

// 错误状态码映射
const ERROR_CODE_MAP = {
    [ParcelIdentificationCode.NO_EXIST]: 'noExist',
    [ParcelIdentificationCode.DELIVERED]: 'delivered',
    [ParcelIdentificationCode.RETURNED]: 'returned',
    [ParcelIdentificationCode.CANCELED]: 'canceled',
    [ParcelIdentificationCode.MISSING]: 'missing',
    [ParcelIdentificationCode.OUTSIDE_CITY_SERVICE_AREA]: 'outsideCityServiceArea',
    [ParcelIdentificationCode.PARCEL_IDENTIFICATION_FAILURE]: 'scanFailure',
    [ParcelIdentificationCode.DRIVER_NOT_VERIFIED]: 'driverNotVerified'
} as const;

// 加载遮罩组件
const LoadingOverlay: React.FC<{ isVisible: boolean }> = ({isVisible}) => {
    if (!isVisible) return null;

    return (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-4 shadow-lg">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"/>
                    <div className="text-gray-700">{t(`identification.processing`)}</div>
                </div>
            </div>
        </div>
    );
};

// 背景遮罩组件
const Backdrop: React.FC<{
    isVisible: boolean;
    onClose: () => void;
    zIndex?: number;
}> = ({isVisible, onClose, zIndex = 40}) => {
    if (!isVisible) return null;

    return (
        <div
            className={`fixed inset-0 z-${zIndex}`}
            onClick={onClose}
        />
    );
};

// 包裹信息卡片组件
const ParcelInfoCard: React.FC<{
    parcel: ParcelInfo;
    parcelCode: string;
    onClose: () => void;
    onNavigateToScan: (parcelCode: string, trackingNumber?: string) => void;
}> = ({parcel,parcelCode, onClose, onNavigateToScan}) => {
    // 统一构造包含 subStopNumber 的展示用 stopNumber
    const displayStopNumber = parcel.subStopNumber ? `${parcel.stopNumber}-${parcel.subStopNumber}` : `${parcel.stopNumber}`;
    return (
        <>
            <Backdrop isVisible={true} onClose={onClose} zIndex={40}/>
            
            {/* 外层容器 - 固定高度以支持滚动 */}
            <div className="mx-2 my-2 bg-gray-100 rounded-lg relative z-50 flex flex-col" style={{ maxHeight: '80vh' }}>
                {/* 可滚动的内容区域 */}
                <div className="flex-1 overflow-y-auto p-2">
                    {/* trackingNumber 和文案区域 - 白色背景凸显 */}
                    <div className="px-6 py-5 bg-white rounded-lg shadow-sm">
                        <div className="text-base font-medium mb-3">{parcel?.trackingNumber}</div>
                        <div className="border-b border-gray-200 mb-3"></div>
                        
                        {/* 文案 - 分为两行显示 */}
                        <div className="text-gray-700">
                            {(() => {
                                // 根据不同的parcelCode显示不同的文案
                                switch (parcelCode) {
                                    case ParcelIdentificationCode.ASSIGNED_TO_CURRENT_DRIVER:
                                        return (
                                            <>
                                                <div className="text-orange-500 font-bold mb-2 text-lg">
                                                    {t(`identification.dialogs.assigned.deliveringPage1`)}
                                                </div>
                                                <div className="text-lg">
                                                    {t(`identification.dialogs.assigned.deliveringPage2`)}
                                                </div>
                                                <div className="text-lg">
                                                    {t(`identification.dialogs.assigned.deliveringPage3`, { stopNumber: displayStopNumber })}
                                                </div>
                                            </>
                                        );
                                    case ParcelIdentificationCode.ASSIGNED_TO_CURRENT_FAILED_DELIVERY:
                                        return (
                                            <>
                                                <div className="text-orange-500 font-bold mb-2 text-lg">
                                                    {t(`identification.dialogs.assigned.failDeliveringPage1`)}
                                                </div>
                                                <div className="text-lg">
                                                    {t(`identification.dialogs.assigned.failDeliveringPage2`)}
                                                </div>
                                                <div className="text-lg">
                                                    {t(`identification.dialogs.assigned.failDeliveringPage3`, { stopNumber: displayStopNumber })}
                                                </div>
                                            </>
                                        );
                                    case ParcelIdentificationCode.ASSIGNED_TO_CURRENT_SCANNED:
                                        return (
                                            <>
                                                <div className="text-orange-500 font-bold mb-2 text-lg">
                                                    {t(`identification.dialogs.assigned.alreadyScan1`)}
                                                </div>
                                                <div className="text-lg">
                                                    {t(`identification.dialogs.assigned.alreadyScan2`)}
                                                </div>
                                                <div className="text-lg">
                                                    {t(`identification.dialogs.assigned.alreadyScan3`, { stopNumber: displayStopNumber })}
                                                </div>
                                            </>
                                        );
                                    default:
                                        return (
                                            <>
                                                <div className="text-orange-500 font-bold mb-2 text-lg">
                                                    {t(`identification.dialogs.assigned.title`)}
                                                </div>
                                                <div className="text-lg">
                                                    {(() => {
                                                        const text = t(`identification.dialogs.assigned.title2`);
                                                        // 匹配引号中的内容
                                                        const parts = text.split(/(".*?")/);
                                                        return parts.map((part, index) => {
                                                            // 如果是引号包裹的内容，加粗显示
                                                            if (part.startsWith('"') && part.endsWith('"')) {
                                                                return <span key={index} className="font-bold">{part}</span>;
                                                            }
                                                            return <span key={index}>{part}</span>;
                                                        });
                                                    })()}
                                                </div>
                                            </>
                                        );
                                }
                            })()}
                        </div>
                    </div>
                </div>
                
                {/* 固定在底部的按钮 */}
                <div className="flex-shrink-0 p-2">
                    <button
                        onClick={() => onNavigateToScan(parcelCode, parcel?.trackingNumber)}
                        className="w-full bg-purple-600 text-white py-4 rounded-lg font-medium text-lg hover:bg-purple-700 transition-colors"
                    >
                        {t(`identification.dialogs.assigned.jump`)}
                    </button>
                </div>
            </div>
        </>
    );
};

// 转单成功卡片组件
const TransferSuccessCard: React.FC<{
    data: TransferSuccessData;
    onClose: () => void;
}> = ({data, onClose}) => {
    const {parcel, recommendDriverName, previousRouteId, previousDriverName, scanTime} = data;

    return (
        <>
            <Backdrop isVisible={true} onClose={onClose}/>
            <Card className="my-2 rounded-lg">
                {/* 标题 */}
                <div className="px-4 py-3 text-center font-semibold text-lg">
                    {t(`identification.dialogs.success.title`)}
                </div>
                {/*运单号*/}
                <div className="flex justify-between items-center">
                    <div className="text-base font-medium">{parcel?.trackingNumber}</div>
                </div>
                <Divider/>
                {/*收件人信息*/}
                <div className="flex gap-6">
                    <div className="flex flex-col items-center justify-center gap-0.5">
                        {(() => {
                            const stopNumber = formatStopNumber(parseInt(parcel?.stopNumber || '0'), parcel?.subStopNumber);
                            return (
                                <div className={`${stopNumber.length > 4 ? 'text-2xl' : 'text-3xl'} font-bold whitespace-nowrap`}>
                                    {stopNumber}
                                </div>
                            );
                        })()}
                        <div className="text-xs">{t('pages.stop')}</div>
                    </div>

                    <div className="flex flex-col gap-1">
                        <h3 className="text-base font-semibold">{parcel?.recipientName}</h3>
                        <p>{maskPhoneNumber(parcel?.phone)}</p>
                        <p>{parcel?.address}</p>
                    </div>
                </div>
                <Divider/>
                {/*包裹信息*/}
                <div className="space-y-2">
                    {recommendDriverName ? (
                        <div className="text-purple-600 leading-normal break-words">
                            <span className="text-purple-600 whitespace-nowrap">
                                {t(`identification.labels.recommendedDriver`)}
                            </span>
                            <span className="font-medium"> {recommendDriverName}</span>
                        </div>
                    ) : null}
                    <div className="flex justify-between">
                        <span className="text-gray-600">{t(`identification.labels.previousRoute`)}</span>
                        <span className="text-gray-800 font-medium">{previousRouteId || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-gray-600">{t(`identification.labels.previousDriver`)}</span>
                        <span className="text-gray-800 font-medium">{previousDriverName || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-gray-600">{t(`identification.labels.scanTime`)}</span>
                        <span className="text-gray-800 font-medium">{scanTime ? dayjs(scanTime).format('YYYY MM-DD HH:mm:ss') : '-'}</span>
                    </div>
                </div>
                {/* OK Button */}
                <div className="pt-4">
                    <button
                        onClick={onClose}
                        className="w-full bg-purple-600 text-white py-4 rounded-lg font-medium text-lg hover:bg-purple-700 transition-colors"
                    >
                        {t(`identification.ok`)}
                    </button>
                </div>
            </Card>
        </>
    );
};

const ParcelIdentificationPage: React.FC = () => {
    const navigate = useNavigate();

    // 状态管理
    const [isProcessing, setIsProcessing] = useState(false);
    const [currentTrackingNumber, setCurrentTrackingNumber] = useState<string>('');
    const currentTrackingNumberRef = useRef<string>('');
    const recommendDriverNameRef = useRef<string | undefined>(undefined);
    
    // 扫描暂停状态：扫描成功后暂停扫描，直到弹窗关闭
    const [isScanPaused, setIsScanPaused] = useState(false);

    // 底部卡片状态
    const [showBottomCard, setShowBottomCard] = useState(false);
    const [currentParcel, setCurrentParcel] = useState<ParcelInfo | null>(null);
    const [currentParcelCode, setCurrentParcelCode] = useState<string | null>(null);
    // 转单成功卡片状态
    const [showTransferSuccessCard, setShowTransferSuccessCard] = useState(false);
    const [transferSuccessData, setTransferSuccessData] = useState<TransferSuccessData | null>(null);

    // 显示错误弹窗
    const showErrorDialog = useCallback((result: ParcelIdentificationResult) => {
        const dialogKey = ERROR_CODE_MAP[result.code as keyof typeof ERROR_CODE_MAP];
        if (!dialogKey) return;

        // 获取原始消息文本
        const originalMessage = t(`identification.dialogs.${dialogKey}.message`);
        
        // 高亮关键词
        const highlightedMessage = originalMessage
            .replace(/(does not exist|delivered|return|canceled|outside this city's service area)/g,
                '<span class="text-orange-500 font-medium">$1</span>')
            .replace(/(不存在|已签收|退件|已被取消|不在本城市的服务区域内)/g,
                '<span class="text-orange-500 font-medium">$1</span>')
            .replace(/(no existe|entregado|devolución|cancelado|fuera del área de servicio de esta ciudad)/g,
                '<span class="text-orange-500 font-medium">$1</span>')
            .replace(/(Please try again.|Please scan other parcel.|Please take parcel back to warehouse and hand over to manager.)/g,
                '<span class="font-bold"><b />$1</span>')
            .replace(/(请重试。|请扫描其他包裹。|请将包裹带回仓库并移交给经理。)/g,
            '<span class="font-bold">$1</span>')
            .replace(/( Por favor, inténtelo de nuevo.|Por favor, escanee otro paquete.|Por favor, lleve el paquete de vuelta al almacén y entréguelo al supervisor.)/g,
                '<span class="font-bold"><b />$1</span>');

        Dialog.alert({
            title: (
                <div className="flex items-center justify-center gap-2">
                    <span>{t(`identification.dialogs.${dialogKey}.title`)}</span>
                </div>
            ),
            content: (
                <div 
                    className="text-gray-700 leading-relaxed text-center"
                    dangerouslySetInnerHTML={{ __html: highlightedMessage }}
                />
            ),
            confirmText: t('identification.cancel'),
            className: CONSTANTS.DIALOG_CLASSES.ERROR,
            onConfirm: () => {
                setIsScanPaused(false); // 恢复扫描
                console.log('关闭错误弹窗，恢复扫描');
            },
        });
    }, []);

    // 显示扫描失败弹窗（未知结果码或识别异常时复用）
    const showScanFailedDialog = useCallback(() => {
        setIsScanPaused(true);
        Dialog.alert({
            title: (
                <div className="flex items-center justify-center gap-2">
                    <span>{t('identification.dialogs.unknownResult.title')}</span>
                </div>
            ),
            content: (
                <div className="text-gray-700 leading-relaxed text-center">
                    {t('identification.dialogs.unknownResult.message')}
                </div>
            ),
            confirmText: t('identification.cancel'),
            className: CONSTANTS.DIALOG_CLASSES.ERROR,
            onConfirm: () => {
                setIsScanPaused(false);
            },
        });
    }, []);

    // 显示包裹信息底部卡片
    const showParcelInfoDialog = useCallback((result: ParcelIdentificationResult) => {
        if (!result.parcel) return;
        setCurrentParcel(result.parcel);
        setCurrentParcelCode(result.code)
        setShowBottomCard(true);
    }, []);

    // 显示转单成功详情卡片
    const showTransferSuccessCardFunction = useCallback((result: IdentifyAndTransferParcelResult) => {
        const {parcel, previousRouteId, previousDriverName, scanTime} = result.transferResult || {};

        setTransferSuccessData({
            parcel,
            recommendDriverName: recommendDriverNameRef.current,
            previousRouteId,
            previousDriverName,
            scanTime
        });
        setShowTransferSuccessCard(true);
    }, []);

    // 处理转单确认
    const handleTransferConfirm = useCallback(async () => {
        const trackingNumber = currentTrackingNumberRef.current;
        console.log('转单确认被点击:', { currentTrackingNumber, trackingNumber, isProcessing });
        if (!trackingNumber || isProcessing) {
            console.log('转单被跳过:', { currentTrackingNumber, trackingNumber, isProcessing });
            return;
        }

        console.log('开始转单:', trackingNumber);
        setIsProcessing(true);

        try {
            const result = await identifyAndTransferParcelV2({
                trackingNumber: trackingNumber,
                scanLocation: CONSTANTS.SCAN_LOCATION,
                confirmTransfer: true
            });

            console.log('转单结果:', result);

            if (result.transferResult?.success) {
                console.log('转单成功');
                Toast.show({
                    icon: 'success',
                    content: t(`identification.dialogs.success.completedMessage`),
                    duration: CONSTANTS.TOAST_DURATION,
                });

                showTransferSuccessCardFunction(result);
                // 注意：不在这里恢复扫描，因为还会显示转单成功卡片
            } else {
                console.error('转单失败:', result.transferResult?.errorMessage);
                Toast.show({
                    icon: 'fail',
                    content: result.transferResult?.errorMessage || t(`identification.dialogs.transfer.transferFailed`),
                });
                // 转单失败时恢复扫描
                setIsScanPaused(false);
                console.log('转单失败，恢复扫描');
            }
        } catch (error) {
            console.error('转单异常:', error);
            Toast.show({
                icon: 'fail',
                content: t(`identification.dialogs.transfer.transferFailed`),
            });
            // 转单异常时恢复扫描
            setIsScanPaused(false);
            console.log('转单异常，恢复扫描');
        } finally {
            console.log('转单处理完成，重置状态');
            setIsProcessing(false);
        }
    }, [isProcessing, showTransferSuccessCardFunction]);

    // 不在配送区域时的确认弹窗
    const showAreaMismatchDialog = useCallback((result: ParcelIdentificationResult) => {
        const recommendDriverName = result.parcel?.recommendDriverName;
        recommendDriverNameRef.current = recommendDriverName;
        Dialog.confirm({
            title: t('identification.dialogs.notInYourArea.title'),
            content: (
                <div className="text-left leading-relaxed space-y-1">
                    <div className="text-orange-500 font-medium">
                        {t('identification.dialogs.notInYourArea.areaMessage')}
                    </div>
                    <div className="text-purple-600 w-full max-w-full break-words leading-normal">
                        <span className="font-medium whitespace-nowrap">
                            {t('identification.dialogs.notInYourArea.recommendDriverLabel')}
                        </span>
                        <span className="font-semibold"> {recommendDriverName || '-'}</span>
                    </div>
                    <div className="text-gray-700">
                        {t('identification.dialogs.notInYourArea.transferHint')}
                    </div>
                </div>
            ),
            // 左边按钮：仍要转入；右边按钮：取消
            cancelText: <span style={{ color: '#000000' }}>{t('identification.dialogs.notInYourArea.transferAnyway')}</span>,
            confirmText: <span style={{ color: '#000000' }}>{t('identification.dialogs.notInYourArea.cancel')}</span>,
            onCancel: handleTransferConfirm,
            onConfirm: () => {
                setIsScanPaused(false);
            },
            className: CONSTANTS.DIALOG_CLASSES.TRANSFER,
        });
    }, [handleTransferConfirm]);

    // 显示转单确认弹窗
    const showTransferDialog = useCallback((result: ParcelIdentificationResult) => {
        if (!result.parcel) return;

        // 根据错误码选择不同的消息文本
        const messageKey = result.code === ParcelIdentificationCode.DSP_MISMATCH 
            ? 'identification.dialogs.transfer.dspMismatchMessage'
            : 'identification.dialogs.transfer.message';
        
        const originalMessage = t(messageKey);
        
        // 高亮关键词
        const highlightedMessage = originalMessage
            .replace(/(Do you want to transfer it to your delivering list\?|您想将其转入您的派送列表吗？|¿Desea transferirlo a su lista de entregas\?)/g,
                '<span class="text-orange-500 font-medium">$1</span>');

        Dialog.confirm({
            title: t(`identification.dialogs.delivered.title`),
            content: (
                <div className="text-center">
                    <div 
                        className="text-gray-700 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: highlightedMessage }}
                    />
                </div>
            ),
            confirmText: t(`identification.transfer`),
            cancelText: t(`identification.cancel`),
            onConfirm: handleTransferConfirm,
            onCancel: () => {
                setIsScanPaused(false); // 取消转单时恢复扫描
                console.log('取消转单，恢复扫描');
            },
            className: CONSTANTS.DIALOG_CLASSES.TRANSFER,
        });
    }, [handleTransferConfirm]);

    // 处理扫描成功
    const handleScanSuccess = useCallback(async (trackingNumber: string) => {
        // 检查是否正在处理中
        if (isProcessing) {
            console.log('扫描被跳过，正在处理中:', trackingNumber);
            return;
        }

        // 检查扫描是否被暂停
        if (isScanPaused) {
            console.log('扫描被跳过，扫描已暂停:', trackingNumber);
            return;
        }

        console.log('开始识别包裹:', trackingNumber);
        setIsProcessing(true);
        setCurrentTrackingNumber(trackingNumber);
        currentTrackingNumberRef.current = trackingNumber;

        try {
            const result = await identifyParcel(trackingNumber);
            console.log('包裹识别结果:', result);

            // 根据结果码显示不同的弹窗，并暂停扫描
            switch (result.code) {
                case ParcelIdentificationCode.NO_EXIST:
                case ParcelIdentificationCode.DELIVERED:
                case ParcelIdentificationCode.RETURNED:
                case ParcelIdentificationCode.CANCELED:
                case ParcelIdentificationCode.MISSING:
                case ParcelIdentificationCode.OUTSIDE_CITY_SERVICE_AREA:
                case ParcelIdentificationCode.PARCEL_IDENTIFICATION_FAILURE:
                case ParcelIdentificationCode.DRIVER_NOT_VERIFIED:
                    console.log('显示错误弹窗:', result.code);
                    showErrorDialog(result);
                    setIsScanPaused(true); // 暂停扫描
                    break;
                case ParcelIdentificationCode.DSP_MISMATCH:
                case ParcelIdentificationCode.TRANSFER_REQUIRED:
                    console.log('显示转单弹窗:', result.code);
                    showTransferDialog(result);
                    setIsScanPaused(true); // 暂停扫描
                    break;
                case ParcelIdentificationCode.NOT_IN_YOUR_DELIVERY_AREA:
                    console.log('显示不在配送区域弹窗:', result.code);
                    showAreaMismatchDialog(result);
                    setIsScanPaused(true); // 暂停扫描
                    break;
                case ParcelIdentificationCode.ASSIGNED_TO_CURRENT_DRIVER:
                case ParcelIdentificationCode.ASSIGNED_TO_CURRENT_TRANSFER_IN:
                case ParcelIdentificationCode.ASSIGNED_TO_CURRENT_UNSCANNED:
                case ParcelIdentificationCode.ASSIGNED_TO_CURRENT_FAILED_DELIVERY:
                case ParcelIdentificationCode.ASSIGNED_TO_CURRENT_SCANNED:
                    console.log('显示包裹信息弹窗:', result.code);
                    showParcelInfoDialog(result);
                    setIsScanPaused(true); // 暂停扫描
                    break;
                default:
                    showScanFailedDialog();
                    break;
            }
        } catch (error) {
            console.error('识别包裹失败:', error);
            showScanFailedDialog();
        } finally {
            console.log('包裹识别处理完成，重置状态');
            setIsProcessing(false);
        }
    }, [isProcessing, isScanPaused, showAreaMismatchDialog, showErrorDialog, showParcelInfoDialog, showTransferDialog, showScanFailedDialog]);

    // 关闭底部卡片
    const closeBottomCard = useCallback(() => {
        setShowBottomCard(false);
        setIsScanPaused(false); // 恢复扫描
        console.log('关闭包裹信息卡片，恢复扫描');
    }, []);

    // 跳转到新分配扫描页面
    const handleNavigateToScan = useCallback(async (currentParcelCode?: string | null,trackingNumber?: string | null) => {
        // 根据不同的currentParcelCode跳转到不同的地址
        switch (currentParcelCode) {
            case ParcelIdentificationCode.ASSIGNED_TO_CURRENT_DRIVER:
                {
                    const deliveringPath = await getDeliveringListPath();
                    navigate(`${deliveringPath}?trackingNumber=${trackingNumber}&from=parcel-identification`);
                }
                break;
            case ParcelIdentificationCode.ASSIGNED_TO_CURRENT_FAILED_DELIVERY:
                {
                    const deliveringPath = await getDeliveringListPath();
                    navigate(`${deliveringPath}?trackingNumber=${trackingNumber}&from=parcel-identification`, {
                        state: { activeTab: 'undelivered' }
                    });
                }
                break;
            case ParcelIdentificationCode.ASSIGNED_TO_CURRENT_UNSCANNED:
                navigate(TaskPath.DELIVERY_TASK_ASSIGNMENTS, {
                    state: { activeTab: 'unscanned' }
                });
                break;
            case ParcelIdentificationCode.ASSIGNED_TO_CURRENT_SCANNED:
                navigate(TaskPath.DELIVERY_TASK_ASSIGNMENTS, {
                    state: { activeTab: 'scanned' }
                });
                break;

            default:
                // 默认跳转到新分配任务页面
                navigate(TaskPath.DELIVERY_TASK_ASSIGNMENTS);
        }
    }, [navigate]);

    // 关闭转单成功卡片
    const closeTransferSuccessCard = useCallback(() => {
        setShowTransferSuccessCard(false);
        setIsScanPaused(false); // 恢复扫描
        console.log('关闭转单成功卡片，恢复扫描');
    }, []);

    // 返回上一页
    const handleBack = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    // 计算是否显示转单成功卡片
    const shouldShowTransferSuccessCard = useMemo(() => {
        return showTransferSuccessCard && transferSuccessData;
    }, [showTransferSuccessCard, transferSuccessData]);

    // 计算是否显示底部卡片
    const shouldShowBottomCard = useMemo(() => {
        return showBottomCard && currentParcel;
    }, [showBottomCard, currentParcel]);

    // 监控 currentTrackingNumber 的变化
    useEffect(() => {
        console.log('currentTrackingNumber 变化:', currentTrackingNumber);
    }, [currentTrackingNumber]);

    return (
        <div className="flex flex-col h-screen bg-white">
            {/* Navbar - 固定在顶部 */}
            <NavBar onBack={handleBack}>
                {t(`pages.useMenu.scanPackage`)}
            </NavBar>

            {/* 主要内容区域 */}
            <div className="flex-1 relative">
                <DynamSoftScanComponent
                    onScanSuccess={handleScanSuccess}
                    showManualInputIcon={true}
                />

                {/* 处理中遮罩 */}
                <LoadingOverlay isVisible={isProcessing}/>
            </div>

            {/* 底部包裹信息卡片*/}
            {shouldShowBottomCard && (
                <ParcelInfoCard
                    parcel={currentParcel!}
                    parcelCode={currentParcelCode!}
                    onClose={closeBottomCard}
                    onNavigateToScan={handleNavigateToScan}
                />
            )}

            {/* 转单成功卡片 */}
            {shouldShowTransferSuccessCard && (
                <TransferSuccessCard
                    data={transferSuccessData!}
                    onClose={closeTransferSuccessCard}
                />
            )}
        </div>
    );
};

export default ParcelIdentificationPage;
